/*eslint no-process-env: 0*/
import ReactGA from 'react-ga4';
import { OCL_CLIENT } from '../common/constants';

const SIGNUP_FLOW_PENDING_KEY = 'signup_flow_pending';

const gaId = () => (typeof window !== 'undefined' && window.GA_ACCOUNT_ID) || process.env.GA_ACCOUNT_ID;
const enabled = () => Boolean(gaId() && gaId() !== 'UA-000000-01');

// No cross-domain linker: every OCL app is a subdomain of openconceptlab.org
// on the same measurement ID, and gtag's default cookie_domain ('auto') puts
// the _ga cookie on .openconceptlab.org, so the client id already carries
// across. send_page_view: false because Analytics sends the page view itself;
// otherwise gtag's config call counts the landing page a second time.
let initialized = false;
const initialize = () => {
  if(initialized || !enabled())
    return;

  ReactGA.initialize(gaId(), { gtagOptions: { send_page_view: false } });
  initialized = true;
};

const GAService = {
  recordPageView(page) {
    if(!enabled())
      return;

    initialize();
    ReactGA.send({ hitType: 'pageview', page });
  },

  recordEvent(name, params) {
    if(!enabled())
      return;

    initialize();
    ReactGA.event(name, { client: OCL_CLIENT, ...params });
  },

  recordBlogRead(post) {
    this.recordEvent('blog_read', { post_slug: post.slug, post_title: post.title, post_category: post.category });
  },

  recordBlogScrollDepth(post, percent) {
    this.recordEvent('blog_scroll_depth', { post_slug: post.slug, percent_scrolled: percent });
  },

  // announcementId is not decoration: it is the banner's campaign identity (and
  // its re-show key), so it changes every time the copy does. Without it on the
  // event, clicks on the launch banner are indistinguishable from clicks on
  // whatever it replaced, and the launch-day question is unanswerable after the
  // fact.
  recordAnnouncementClick(announcementId, linkUrl) {
    this.recordEvent('announcement_click', { announcement_id: announcementId, link_url: linkUrl });
  },

  recordAnnouncementDismiss(announcementId) {
    this.recordEvent('announcement_dismiss', { announcement_id: announcementId });
  },

  // A completed early-access request (ocl_online#170), sent only once Brevo
  // has accepted it. GA4's recommended lead event; lead_source is its
  // recommended parameter. Never a name, email, organization, username or
  // message: Google's policy bans personal data in Analytics, and the
  // request itself lives in Brevo. The link's ?source= is the only
  // structured context sent.
  recordLead({ source }) {
    this.recordEvent('generate_lead', { lead_source: source });
  },

  // The site's Sign up buttons go to the API's signup page and come back to
  // /oidc/login here, so the funnel is recorded on this site. The flag
  // survives that same-tab round trip and tells a finished signup apart from
  // an ordinary sign-in, which lands on the same callback.
  recordSignupStart() {
    sessionStorage.setItem(SIGNUP_FLOW_PENDING_KEY, '1');
    this.recordEvent('signup_start');
  },

  clearSignupFlow() {
    sessionStorage.removeItem(SIGNUP_FLOW_PENDING_KEY);
  },

  recordSignupComplete() {
    const pending = sessionStorage.getItem(SIGNUP_FLOW_PENDING_KEY) === '1';
    this.clearSignupFlow();
    if(pending)
      this.recordEvent('signup_complete');
  },

  // The email-verification link finishes sign-up in a tab that never called
  // recordSignupStart, so the flag above isn't there to see.
  recordSignupVerified() {
    this.clearSignupFlow();
    this.recordEvent('signup_complete', { completed_via: 'email_link' });
  },
};

export default GAService;
